import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { clearUser, type User } from '../lib/auth'
import { api } from '../lib/api'
import LangSwitcher from './LangSwitcher'
import { useI18n } from '../lib/i18n'

export default function Navbar({ user, onSignOut }: { user: User | null; onSignOut: () => void }) {
  const [open, setOpen] = useState(false)
  const { t } = useI18n()
  const nav = useNavigate()
  const location = useLocation()
  const path = location.pathname.length > 1 && location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname
  const isHome = path === '/' || path === '/catalog' || path === '/pricing'

  async function handleSignOut() {
    try {
      await api.post('/api/auth/logout', {})
    } catch {
      /* ignore, still clear locally */
    }
    clearUser()
    onSignOut()
    nav('/')
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
      isHome
        ? isActive
          ? 'text-[#071023] bg-[#eef2f8]'
          : 'text-[#7e889a] hover:text-[#071023] hover:bg-[#eef2f8]'
        : isActive
          ? 'text-white bg-ink-800'
          : 'text-ink-100 hover:text-white hover:bg-ink-800/50'
    }`

  return (
    <header className={`fixed top-0 inset-x-0 z-40 backdrop-blur border-b ${
      isHome ? 'bg-[#f7f9fd]/90 border-[#dfe5ef]' : 'bg-ink-950/80 border-ink-800'
    }`}>
      <div className="section flex items-center justify-between h-16">
        <Link to="/" className={`flex items-center gap-3 font-black text-lg ${isHome ? 'text-[#071023]' : 'text-white'}`}>
          <span className={`inline-grid place-items-center w-8 h-8 rounded-lg text-sm shadow-lg ${
            isHome ? 'bg-[#ffb21f] text-[#211300] shadow-amber-200' : 'bg-gradient-to-br from-brand-500 to-brand-700 text-white'
          }`}>Q</span>
          <span>
            Qordo
            {isHome && <span className="block -mt-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#9ba6b8]">Catalog</span>}
            {!isHome && <span> Catalog</span>}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/catalog" className={linkClass}>{t('Catalog')}</NavLink>
          {isHome ? (
            <>
              <NavLink to="/pricing" className={linkClass}>{t('Pricing')}</NavLink>
              <NavLink to="/how-it-works" className={linkClass}>{t('For Partners')}</NavLink>
              <NavLink to="/story" className={linkClass}>{t('Watch the story')}</NavLink>
              {user && <NavLink to="/dashboard" className={linkClass}>{t('Dashboard')}</NavLink>}
              {user?.role === 'admin' && <NavLink to="/admin" className={linkClass}>Admin</NavLink>}
            </>
          ) : (
            <>
              <NavLink to="/marketplace" className={linkClass}>{t('Marketplace')}</NavLink>
              <NavLink to="/pricing" className={linkClass}>{t('Pricing')}</NavLink>
              <NavLink to="/how-it-works" className={linkClass}>{t('How it works')}</NavLink>
              {user && <NavLink to="/dashboard" className={linkClass}>{t('Dashboard')}</NavLink>}
              {user?.role === 'admin' && <NavLink to="/admin" className={linkClass}>Admin</NavLink>}
            </>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <LangSwitcher light={isHome} />
          {isHome && user ? (
            <Link to="/dashboard" className="flex items-center gap-3 rounded-full border border-[#dfe5ef] bg-white px-3 py-2 shadow-sm">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-[#ffb21f] text-xs font-black text-[#211300]">{user.fullName?.slice(0, 2).toUpperCase() || 'QP'}</span>
              <span className="leading-tight">
                <span className="block text-sm font-black text-[#071023]">{user.fullName || 'Partner'}</span>
                <span className="block text-[11px] font-black text-[#f59e0b]">Dashboard →</span>
              </span>
            </Link>
          ) : user ? (
            <>
              <span className="text-xs text-ink-500 hidden lg:inline">
                Code: <span className="font-mono text-brand-500">{user.referralCode}</span>
              </span>
              <button onClick={handleSignOut} className={isHome ? 'rounded-xl px-4 py-2 text-sm font-black text-[#071023] hover:bg-[#eef2f8]' : 'btn-ghost text-sm py-2'}>{t('Sign out')}</button>
            </>
          ) : (
            <>
              <Link to="/login" className={isHome ? 'rounded-xl px-4 py-2 text-sm font-black text-[#6d788d] hover:bg-[#eef2f8] hover:text-[#071023]' : 'btn-ghost text-sm py-2'}>{t('Login')}</Link>
              <Link to="/signup" className={isHome ? 'rounded-xl bg-[#071023] px-4 py-2 text-sm font-black text-white hover:bg-[#111b34]' : 'btn-primary text-sm py-2'}>{t('Join as partner')}</Link>
            </>
          )}
        </div>

        <button
          aria-label="Menu"
          onClick={() => setOpen((x) => !x)}
          className={`md:hidden p-2 rounded-lg ${isHome ? 'text-[#071023] hover:bg-[#eef2f8]' : 'hover:bg-ink-800'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {open && (
        <nav className={`md:hidden border-t px-4 py-3 space-y-1 ${isHome ? 'border-[#dfe5ef] bg-white' : 'border-ink-800 bg-ink-950'}`}>
          <NavLink to="/catalog" className={linkClass} onClick={() => setOpen(false)}>{t('Catalog')}</NavLink>
          <NavLink to="/marketplace" className={linkClass} onClick={() => setOpen(false)}>{t('Marketplace')}</NavLink>
          <NavLink to="/pricing" className={linkClass} onClick={() => setOpen(false)}>{t('Pricing')}</NavLink>
          <NavLink to="/how-it-works" className={linkClass} onClick={() => setOpen(false)}>{t('How it works')}</NavLink>
          {user && <NavLink to="/dashboard" className={linkClass} onClick={() => setOpen(false)}>{t('Dashboard')}</NavLink>}
          {user?.role === 'admin' && <NavLink to="/admin" className={linkClass} onClick={() => setOpen(false)}>Admin</NavLink>}
          <div className="pt-3 pb-1"><LangSwitcher light={isHome} /></div>
          <div className="pt-3 border-t border-ink-800 flex gap-2">
            {user ? (
              <button onClick={handleSignOut} className="btn-secondary flex-1 text-sm">{t('Sign out')}</button>
            ) : (
              <>
                <Link to="/login" className="btn-secondary flex-1 text-sm" onClick={() => setOpen(false)}>{t('Login')}</Link>
                <Link to="/signup" className="btn-primary flex-1 text-sm" onClick={() => setOpen(false)}>{t('Join')}</Link>
              </>
            )}
          </div>
        </nav>
      )}
    </header>
  )
}
